export const PremiumDeliveryOptions = {
  Premium: 'Premium Delivery',
  UnpackAndConnect: 'Unpack & Connect',
  PremiumDeliverAndRemove: 'Deliver and remove',
  PremiumDeliverAndUnpack: 'Deliver and unpack',
  PremiumDeliverUnpackAndRemove: 'Deliver, unpack and remove',
  PremiumDeliveryUnpackAndConnect: 'Deliver, unpack and connect',
  PremiumDeliverUnpackConnectAndRemove: 'Deliver, unpack, connect and remove',
};

export const premiumDeliveryOptionValues = Object.values(PremiumDeliveryOptions);

export const productTypes = {
  fridge: {
    name: 'fridge',
    titles: [
      'REFRIGERATION- BAR FRIDGE',
      'REFRIGERATION- SINGLE DOOR',
      'REFRIGERATION- TOP MOUNT',
      'REFRIGERATION- SIDE BY SIDE',
      'REFRIGERATION- UPRIGHT FREEZER',
      'REFRIGERATION- CHEST FREEZER',
      'REFRIGERATION- WINE CABINET',
      'REFRIGERATION- FRENCH DOOR',
      'REFRIGERATION- BOTTOM MOUNT',
      'REFRIGERATION- FRENCH DOOR DISPENSER',
    ],
  },
  washer: {
    name: 'washer',
    titles: [
      'LAUNDRY - TOP LOAD WASHERS',
      'LAUNDRY - FRONT LOAD WASHER',
      'LAUNDRY - WASHER / DRYER PACK',
      'LAUNDRY - TWIN TUB WASHER',
    ],
  },
  dishwasher: {
    name: 'dishwasher',
    titles: [
      'DISHWASHER - BUILT IN FREESTANDING',
      'DISHWASHER',
      'DISHWASHERS- SEMI AND FULLY INTERG',
      'DISHWASHER - DRAWERS',
      'DISHWASERS SLIME LINE - BENCHTOP',
    ],
  },
};

export const getProductType = (productGroup: string) => {
  const product = productGroup?.replace(/ /g, '')?.toLowerCase();
  if (productTypes.washer?.titles?.some(title => product?.includes(title?.replace(/ /g, '')?.toLowerCase()))) {
    return productTypes?.washer.name;
  } else if (productTypes.fridge?.titles?.some(title => product?.includes(title?.replace(/ /g, '')?.toLowerCase()))) {
    return productTypes.fridge.name;
  } else if (
    productTypes.dishwasher?.titles?.some(title => product?.includes(title?.replace(/ /g, '')?.toLowerCase()))
  ) {
    return productTypes.dishwasher.name;
  }
  return 'default';
};

export type PremiumDeliveryFeature = {
  experiences: Array<string>;
  heading: string;
  content?: string;
  showOnlyFor?: Array<string>;
  excludeExperiences?: Array<string>;
  pages?: Array<string>;
};

export const premiumDeliveryContent = {
  unpackAndConnect: [
    { feature: 'Unbox goods' },
    { feature: 'Connect product to power and water if applicable' },
    { feature: 'Take away and recycle all packaging' },
  ],
  premiumDelivery: [
    { feature: 'Everything included in Unpack & Connect' },
    { feature: 'Plus removal of your old product of similar type and size' },
  ],
  deliverAndRemove: [{ feature: 'Get your old disconnected product of a similar type and size taken away' }],
  deliverAndUnpack: [
    { feature: 'Unpack product' },
    { feature: 'Position and plug-in to existing power' },
    { feature: 'Take away packaging' },
  ],
  deliverUnpackAndRemove: [
    { feature: 'Everything included in Deliver and unpack' },
    {
      feature: 'Plus get your old disconnected product of a similar type and size taken away',
    },
  ],
  deliverUnpackAndConnect: [
    { feature: 'Unpack product' },
    { feature: 'Connect water and run a test cycle' },
    { feature: 'Take away packaging' },
  ],
  deliverUnpackConnectAndRemove: [
    { feature: 'Everything included in Deliver and unpack' },
    { feature: 'Connect water and run a test cycle', showOnlyFor: ['washer'] },
    { feature: 'Plus removal of your old product of a similar type and size' },
  ],
};

export const premiumDeliveryOptions = [
  { title: PremiumDeliveryOptions.UnpackAndConnect, description: premiumDeliveryContent.unpackAndConnect },
  { title: PremiumDeliveryOptions.Premium, description: premiumDeliveryContent.premiumDelivery },
  { title: PremiumDeliveryOptions.PremiumDeliverAndRemove, description: premiumDeliveryContent.deliverAndRemove },
  { title: PremiumDeliveryOptions.PremiumDeliverAndUnpack, description: premiumDeliveryContent.deliverAndUnpack },
  {
    title: PremiumDeliveryOptions.PremiumDeliveryUnpackAndConnect,
    description: premiumDeliveryContent.deliverUnpackAndConnect,
  },
  {
    title: PremiumDeliveryOptions.PremiumDeliverUnpackAndRemove,
    description: premiumDeliveryContent.deliverUnpackAndRemove,
  },
  {
    title: PremiumDeliveryOptions.PremiumDeliverUnpackConnectAndRemove,
    description: premiumDeliveryContent.deliverUnpackConnectAndRemove,
  },
];

const commonFeatures = {
  setupInternalShelving: {
    heading: 'Set up any internal shelving and drawers',
    content: `For included components that don't require tools e.g. placement of internal shelves and drawers`,
    showOnlyFor: ['fridge'],
  },
  plugInConnect: {
    heading: 'Plug-in and power on product',
    content: '',
    showOnlyFor: [],
  },
  removalSuppliedPackage: {
    heading: 'Removal of all supplied packaging',
    showOnlyFor: [],
  },
  wallMounting: {
    experiences: [],
    heading: 'Wall mounting or installation into cabinetry or fixtures',
    content: '',
    showOnlyFor: [],
  },
  removeOldProduct: {
    experiences: ['deliver and remove', 'deliver, unpack and remove', 'deliver, unpack, connect and remove'],
    excludeExperiences: ['deliver and unpack'],
    heading: 'Remove old product of similar type and size',
    showOnlyFor: [],
  },
};

export const premiumDeliveryFeatures: Array<PremiumDeliveryFeature> = [
  {
    experiences: ['unpack & connect'],
    heading: 'Unbox and remove packaging',
    content: 'Including any transit brackets, bolts or restraints such as securing tape or strapping.',
    showOnlyFor: [],
    pages: ['popout'],
  },
  {
    experiences: ['unpack & connect'],
    heading: 'Connect your product to existing power and plumbing (if applicable)',
    content: 'Using supplied power cords, hoses and other connections supplied with your product.',
    showOnlyFor: [],
    pages: ['popout'],
  },
  {
    experiences: ['unpack & connect'],
    heading: 'Ensure the product is level on the floor',
    content: '',
    showOnlyFor: [],
    pages: ['popout'],
  },
  {
    experiences: ['unpack & connect'],
    heading: 'Take away and recycle all packaging',
    content: '',
    showOnlyFor: [],
    pages: ['popout'],
  },
  {
    experiences: ['premium delivery'],
    heading: 'Everything included in Unpack & Connect',
    content: '',
    showOnlyFor: [],
    pages: ['popout'],
  },
  {
    experiences: ['premium delivery'],
    heading: 'We’ll remove your old product of a similar type and size',
    content: '',
    showOnlyFor: [],
    pages: ['popout'],
  },
  {
    experiences: ['unpack & connect', 'premium delivery'],
    heading: 'Delivery of product to room of choice',
    content: '',
    showOnlyFor: [],
    pages: ['checkout'],
  },
  {
    experiences: [
      'deliver and unpack',
      'deliver, unpack and remove',
      'deliver, unpack and connect',
      'deliver, unpack, connect and remove',
    ],
    heading: 'Deliver product to room of choice',
    content: '',
    showOnlyFor: [],
    pages: ['checkout'],
  },
  {
    experiences: ['deliver and remove', 'deliver and unpack', 'deliver, unpack and connect'],
    heading: 'Deliver product to room of choice',
    content: '',
    showOnlyFor: [],
    pages: ['popout'],
  },
  {
    experiences: [
      'unpack & connect',
      'premium delivery',
      'deliver and unpack',
      'deliver, unpack and remove',
      'deliver, unpack and connect',
      'deliver, unpack, connect and remove',
    ],
    heading: 'Unpack product',
    content: '',
    showOnlyFor: [],
    pages: ['checkout'],
  },
  {
    experiences: ['deliver and unpack', 'deliver, unpack and connect'],
    heading: 'Unpack product',
    content: '',
    showOnlyFor: [],
    pages: ['popout'],
  },

  {
    experiences: ['unpack & connect', 'premium delivery'],
    heading: 'Basic setup',
    content:
      "For included components that don't require tools e.g. placement of internal shelves, drawers and attach handles.",
    showOnlyFor: [],
    pages: ['checkout'],
  },
  {
    experiences: ['unpack & connect', 'premium delivery'],
    heading: 'Plug-in power cord and hoses that come with your product',
    content: '',
    showOnlyFor: [],
    pages: ['checkout'],
  },
  {
    experiences: ['unpack & connect', 'premium delivery'],
    heading: 'Power on product and turn taps on',
    content: '',
    showOnlyFor: [],
    pages: ['checkout'],
  },
  {
    experiences: ['unpack & connect', 'premium delivery'],
    heading: 'Removal and recycle of all supplied packaging',
    content: '',
    showOnlyFor: [],
    pages: ['checkout'],
  },
  {
    experiences: ['premium delivery'],
    heading: 'Remove old product of similar type and size',
    excludeExperiences: ['unpack & connect', 'deliver, unpack and connect'],
    content: '',
    showOnlyFor: [],
    pages: ['checkout'],
  },
  {
    experiences: ['deliver and unpack', 'deliver, unpack and remove'],
    ...commonFeatures.setupInternalShelving,
    pages: ['checkout'],
  },
  {
    experiences: ['deliver and unpack'],
    ...commonFeatures.setupInternalShelving,
    pages: ['popout'],
  },
  {
    experiences: [
      'deliver and unpack',
      'deliver, unpack and remove',
      'deliver, unpack and connect',
      'deliver, unpack, connect and remove',
    ],
    ...commonFeatures.plugInConnect,
    pages: ['checkout'],
  },
  {
    experiences: ['deliver and unpack', 'deliver, unpack and connect'],
    ...commonFeatures.plugInConnect,
    pages: ['popout'],
  },
  {
    experiences: [
      'deliver and unpack',
      'deliver, unpack and remove',
      'deliver, unpack and connect',
      'deliver, unpack, connect and remove',
    ],
    ...commonFeatures.removalSuppliedPackage,
    pages: ['checkout'],
  },

  {
    experiences: ['deliver and unpack', 'deliver, unpack and connect'],
    ...commonFeatures.removalSuppliedPackage,
    content: 'Including tapes, straps, transit bolts, brackets or other transportation restraints.',
    pages: ['popout'],
  },
  {
    experiences: ['deliver, unpack and remove', 'deliver, unpack, connect and remove'],
    heading: 'Everything included in Deliver and unpack',
    content: '',
    showOnlyFor: [],
    pages: ['popout'],
  },
  {
    experiences: ['deliver, unpack and remove', 'deliver, unpack and connect', 'deliver, unpack, connect and remove'],
    excludeExperiences: ['deliver and unpack'],
    heading: 'Connect water and run a test cycle',
    content: '',
    showOnlyFor: ['washer'],
    pages: ['checkout', 'popout'],
  },
  {
    ...commonFeatures.removeOldProduct,
    pages: ['checkout'],
  },
  {
    ...commonFeatures.removeOldProduct,
    content: 'Old product must be disconnected, empty and clean ready for removal at the time of your delivery.',
    pages: ['popout'],
  },
  {
    experiences: [],
    excludeExperiences: [
      'deliver and unpack',
      'deliver, unpack and remove',
      'deliver, unpack, connect and remove',
      'deliver and remove',
    ],
    heading: 'Connect water to plumbing',
    content: '',
    showOnlyFor: ['fridge', 'dishwasher'],
    pages: ['checkout'],
  },
  {
    experiences: [],
    heading: 'Demonstrate product functionality',
    content: '',
    showOnlyFor: [],
    pages: ['checkout', 'popout'],
  },

  {
    ...commonFeatures.wallMounting,
    pages: ['checkout'],
  },
  {
    ...commonFeatures.wallMounting,
    content:
      'Our delivery partners do not install Home products on stacking kits or walls, even if there is an existing fixture.',
    pages: ['popout'],
  },
];

export const getPremiumDeliveryFeatures = ({
  productType = 'default',
  page,
}: {
  productType?: string | null;
  page: string;
}) => {
  const featuresForPage = premiumDeliveryFeatures?.filter(feature => feature?.pages?.includes(page));
  return featuresForPage?.filter(
    (feature: any) => feature.showOnlyFor?.length === 0 || feature.showOnlyFor?.includes(productType)
  );
};
